import Roadmap16 from '@carbon/icons-react/es/roadmap/16';
import styled, { css } from 'styled-components';
import { Button } from 'components/atoms/button';
import { Checkbox } from 'components/atoms/checkbox';
import {
  EmptyStateContainer as RawEmptyStateContainer,
  EmptyState as RawEmptyState,
} from 'components/atoms/empty-state';
import { BodyShort01, Caption01, SingleLineEllipsis } from 'components/styles';

export const SpacedButton = styled(Button)`
  display: flex;
  justify-content: space-between;
  padding: var(--spacing-04) var(--spacing-05);
  gap: var(--spacing-03);
`;

export const PhaseNameWrapper = styled.div`
  ${SingleLineEllipsis};
  ${BodyShort01};
  color: var(--gray-50);
`;

export const ShowSavedTemplates = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-04);
`;

export const TemplateWithDate = styled(ShowSavedTemplates)`
  width: 210px;
`;

export const FooterText = styled.div`
  margin-top: var(--spacing-02);
  color: var(--gray-50);
`;

export const Divider = styled.hr`
  color: var(--gray-20);
  opacity: 0.3;
  height: 1px;
  width: 100%;
  margin: 0 auto;
`;

export const ButtonGroup = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  gap: var(--spacing-05);
  padding: var(--spacing-04) 0 0;

  ${Button} {
    flex: 1;
  }
`;

export const PopoverContent = styled.div`
  width: 336px;
`;

export const Spacer = styled.div`
  padding: var(--spacing-04) var(--spacing-05) 0;
`;

export const UsedTemplateCaption = styled.div`
  color: var(--gray-50);
`;

export const EmptyStateContainer = styled(RawEmptyStateContainer)`
  padding: var(--spacing-10) var(--spacing-05);
  height: 100%;
`;

export const EmptyState = styled(RawEmptyState)`
  width: auto;
`;

export const DrawerSectionHeader = styled.div`
  display: flex;
  background-color: var(--coolest-gray-20);
  color: var(--gray-90);
  padding: 0 var(--spacing-04);
  height: var(--spacing-07);
`;

export const TaskDatePanel = styled.div`
  display: flex;
  align-items: center;
  justify-self: flex-start;
  width: 100%;
`;

export const BoldTextWrapper = styled.div`
  font-weight: var(--font-weight-bold);
  width: 100%;
`;
export const DatePickerWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  height: var(--spacing-09);
  padding: 0 var(--spacing-04);
  border-top: 1px solid var(--border-color);
  &:hover {
    background-color: var(--blue-10);
  }
`;

export const DatePickerTaskName = styled.div`
  display: flex;
  align-items: center;
  width: 65%;
`;

export const ScrollableView = styled.div`
  position: relative;
  overflow-x: hidden;
  overflow-y: auto;
  flex: 1;
`;

export const DatePicker = styled.div<{ highlight: boolean }>`
  .ant-btn-text {
    color: ${({ highlight }) => (highlight ? 'var(--yellow-70)' : 'initial')};
  }

  .ant-btn {
    padding: 0;
  }
`;

export const PhaseDurationStatus = styled.div`
  text-align: right;
  display: flex;
  flex-direction: column;
`;

export const CurrentStatus = styled.div``;

type WarningProps = {
  showWarning?: boolean;
};

export const NewStatus = styled.div<WarningProps>`
  color: ${(props) => (props.showWarning ? 'var(--yellow-70)' : 'initial')};
`;

export const SearchInput = styled.div`
  margin: var(--spacing-05);
  position: relative;
  svg {
    position: absolute;
    right: var(--spacing-05);
    top: var(--spacing-04);
  }
  input {
    padding-right: var(--spacing-08);
  }
`;

export const TemplateNameHeader = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  padding: var(--spacing-05);
  align-items: center;
  height: var(--spacing-09);
`;

export const TemplateEllipsis = styled.strong`
  ${SingleLineEllipsis};
  width: 305px;
`;

export const RoundedCheckbox = styled(Checkbox)`
  margin-right: var(--spacing-03);

  .ant-checkbox-inner {
    border-radius: 50%;
  }
  .ant-checkbox:hover::after {
    border-radius: 50%;
  }
`;

export const TaskTemplateHeader = styled.div`
  display: flex;
  justify-content: space-between;
  padding: var(--spacing-05);
  align-items: center;
`;

export const TemplateItem = styled(TemplateNameHeader)<{
  selected?: boolean;
}>`
  cursor: pointer;
  justify-content: space-between;
  gap: var(--spacing-03);
  border-top: 1px solid var(--border-color);

  &:hover {
    background-color: var(--blue-10);
  }

  &:hover ${RoundedCheckbox} {
    visibility: visible;
  }

  ${({ selected }) =>
    selected
      ? css`
          ${RoundedCheckbox} {
            visibility: visible;
          }
        `
      : css`
          ${RoundedCheckbox} {
            visibility: hidden;
          }
        `};
`;

export const RoadMap = styled(Roadmap16)`
  fill: var(--green-40);
`;

export const TriggerButton = styled(Button)`
  padding-right: var(--spacing-03);
  padding-left: var(--spacing-03);
  max-width: 80%;
  span {
    ${SingleLineEllipsis};
  }
`;

export const EmptyTemplateList = styled.div`
  ${Caption01};
  position: absolute;
  bottom: var(--spacing-05);
  color: var(--gray-70);
  padding: 0 var(--spacing-05);
  width: 80%;

  > svg {
    margin-bottom: var(--spacing-05);
  }
  ${Button} {
    padding-left: 0;
    padding-right: 0;
    margin-top: var(--spacing-08);
  }
`;

export const SelectContainer = styled.div`
  min-height: 350px;
  width: 272px;
  display: flex;
  flex-direction: column;
`;

export const AllTemplatesText = styled.div`
  margin-bottom: var(--spacing-04);
  color: var(--blue-60);
`;

export const TemplateSection = styled.div`
  margin-bottom: var(--spacing-05);
`;

export const Name = styled.div`
  ${SingleLineEllipsis};
`;

export const TaskTemplateName = styled(Name)`
  width: 260px;
`;
export const TaskTemplateNameWithDate = styled(Name)`
  width: 160px;
`;
